import * as React from 'react';
import { buildClassString } from '@ps/payscale-shared-ui/lib/StringUtils';
import { throttle } from '@ps/payscale-shared-ui/lib/TimingUtils';
import './tableComponent.scss';

const enum SortDirection {
    None = 0,
    Ascending,
    Descending
}

export interface ColumnInfo<T> {
    allowSort?: boolean; // whether or not the table can be sorted by this column
    grow?: boolean;      // whether or not the column should grow to take up extra white space
    heading?: string;    // heading to display in table header

    // callback to render column data, where item<T> is the data for the entire
    // row.  The callback is responsible for extracting the appropriate item to render
    renderCallback: (item: T) => React.ReactNode;
    compareCallback?: (item1: T, item2: T) => number;
}

interface TableComponentProps<T> {
    className?: string;             // classname to add to parent div
    columns: Array<ColumnInfo<T>>;  // columns to display in table
    data: T[];                      // data to display in table
}

interface TableComponentState<T> {
    columnWidths: number[];         // pixel width of columns, based on content in all rows
    sortColumn: number;             // current column we're sorted on, -1 if no sort
    sortDirection: SortDirection;   // method of sorting column: ascending, descending, etc
    sortedData: T[];                // copy of data, in sorted state
}

interface Stringable {
    toString: () => string;
}

class TableComponent<T extends Stringable >
    extends React.PureComponent<TableComponentProps<T>, TableComponentState<T>> {
    private _bodyRef: HTMLDivElement | null;
    private _headerRef: HTMLDivElement | null;
    private _scrollPlaceholderRef: HTMLDivElement | null;
    private _bodyScrollCallback: EventListener;
    private _resizeCallback: EventListener;

    constructor(props: TableComponentProps<T>) {
        super(props);
        this.state = Object.assign(
            {},
            this.state,
            {
                columnWidths: [],
                sortColumn: -1,
                sortDirection: SortDirection.None
            });
    }

    componentDidMount(): void {
        // after the component has mounted, update column widths
        this._updateColumnWidths();

        if (this._bodyRef) {
            this._bodyScrollCallback = throttle(() => this._onBodyScroll());
            this._bodyRef.addEventListener('scroll', this._bodyScrollCallback);
        }

        // watch for resize events so we can check for vertical scrollbar in body
        // and adjust header
        this._resizeCallback = throttle(() => this._adjustForScrollbar());
        window.addEventListener('resize', this._resizeCallback);
    }

    componentWillUnmount(): void {
        if (this._bodyRef) {
            this._bodyRef.removeEventListener('scroll', this._bodyScrollCallback);
        }
        window.removeEventListener('resize', this._resizeCallback);
    }

    componentDidUpdate(prevProps: TableComponentProps<T>): void {
        const { sortColumn, sortDirection } = this.state;
        if (prevProps.data !== this.props.data || prevProps.columns !== this.props.columns) {
            // if our data or column definitions have changed, update column widths
            this._updateColumnWidths();

            // and also re-sort data
            this._sortData(sortColumn, sortDirection);

            // and check for scrollbar
            this._adjustForScrollbar();
        }
    }

    render(): React.ReactNode {
        const { className, columns, data } = this.props;
        const { sortedData } = this.state;

        let classString = 'table-component';
        if (!!className) {
            classString += ` ${className}`;
        }

        return (
            <div className={classString}>
                {this._renderHeader(columns)}
                {this._renderBody(columns, sortedData?.length ? sortedData : data)}
            </div>
        );
    }

    private _computeCellStyleForColumn(column: ColumnInfo<T>, columnIdx: number): object {
        const { columnWidths } = this.state;

        // render with a fixed width if this column isn't set to grow/take up extra space
        return !column.grow && columnWidths[columnIdx] ?
            { width: `${columnWidths[columnIdx]}px`} :
            { };
    }

    private _renderHeader(columns: Array<ColumnInfo<T>>): React.ReactNode {
        const { sortColumn, sortDirection } = this.state;
        const renderColumn = (column: ColumnInfo<T>, idx: number) => {

            const classString = buildClassString({
                'table-component__header-cell': true,
                'grow': !!column.grow
            });

            const clickHandler = column.allowSort ?
                () => this._toggleSort(idx) :
                undefined;

            let sortIconClass = '';
            if (column.allowSort) {
                // always render an icon, even if this isn't the currently sorted column.
                // The CSS will set it to be visibility:hidden so it allocates space to ensure
                // our columns don't resize as we sort
                sortIconClass = sortDirection === SortDirection.None || sortColumn !== idx ?
                    'icon-sort' :
                    sortDirection === SortDirection.Ascending ? 'icon-sort-up' : 'icon-sort-down';
            }

            return (
                <div
                    className={classString}
                    key={`${column.heading}_${idx}`}
                    style={this._computeCellStyleForColumn(column, idx)}
                    onClick={clickHandler}
                >
                    {column.heading}
                    {sortIconClass && <i className={`table-component__sort-icon ${sortIconClass}`} />}
                </div>
            );
        };

        return (
            <div className="table-component__header" ref={ref => this._headerRef = ref}>
                {columns && columns.map(renderColumn)}
                <div
                    className="table-component__header__scrollbar-placeholder"
                    ref={ref => this._scrollPlaceholderRef = ref}
                />
                {/* above is a placeholder to shift header if body has vertical scrollbar */}
            </div>
        );
    }

    private _renderBody(columns: Array<ColumnInfo<T>>, data: T[]): React.ReactNode {
        return (
            <div
                className="table-component__body"
                ref={ref => this._bodyRef = ref}
            >
                {data && data.map((item, idx) => this._renderRow(columns, item, idx))}
            </div>
        );
    }

    private _renderRow(columns: Array<ColumnInfo<T>>, item: T, rowNum: number): React.ReactNode {
        if (!columns || !item) {
            return null;
        }

        return (
            <div className="table-component__row" key={`row_${rowNum}`}>
                {columns.map((column, idx) => {
                    const classString = buildClassString({
                        'table-component__body-cell': true,
                        'grow': !!column.grow
                    });

                    return(
                        <div
                            className={classString}
                            key={`cell_${rowNum}_${idx}`}
                            style={this._computeCellStyleForColumn(column, idx)}
                        >
                            {column.renderCallback ?
                                column.renderCallback(item) :
                                item.toString()}
                        </div>
                    );
                })}
            </div>
        );
    }

    // keeps header scroll offset in sync with body (as only body shows scrollbar)
    private _onBodyScroll(): void {
        // when the body scrolls, make sure we keep the header aligned
        if (this._headerRef && this._bodyRef) {
            this._headerRef.scrollLeft = this._bodyRef.scrollLeft;
        }
    }

    // Checks for vertical scrollbar in body, and adjusts placeholder in header
    // to keep columns aligned
    private _adjustForScrollbar(): void {
        if (this._bodyRef) {
            const clientWidth = this._bodyRef.clientWidth;
            const offsetWidth = this._bodyRef.offsetWidth;
            const delta = offsetWidth - clientWidth;
            if (this._scrollPlaceholderRef) {
                this._scrollPlaceholderRef.style.width = `${delta}px`;
            }
        }
    }

    // To be called whenever table content changes -- updates widths of columns
    // based on data in all rows
    private _updateColumnWidths(): void {
        if (!this._bodyRef || !this._headerRef) {
            // if we don't have the ref, nothing we can do
            return;
        }

        const { columns } = this.props;
        const newWidths = new Array<number>(columns.length);

        // iterate over columns in header first
        const headerCols: HTMLCollectionOf<HTMLElement> = this._headerRef.children as HTMLCollectionOf<HTMLElement>;
        for (let colIdx = 0; colIdx < columns.length; colIdx++) {
            newWidths[colIdx] = columns[colIdx].grow ? 0 : headerCols[colIdx].scrollWidth;
        }

        // Iterate over each row, and then each cell in
        // that row to get width and compare against stored off width
        const rows: HTMLCollectionOf<HTMLElement> = this._bodyRef.children as HTMLCollectionOf<HTMLElement>;
        for (const row of rows) {
            const cells: HTMLCollectionOf<HTMLElement> = row.children as HTMLCollectionOf<HTMLElement>;
            for (let colIdx = 0; colIdx < columns.length; colIdx++) {
                if (columns[colIdx].grow) {
                    continue;
                }

                const cellWidth = cells[colIdx].scrollWidth;
                if (!newWidths[colIdx] || cellWidth > newWidths[colIdx]) {
                    newWidths[colIdx] = cellWidth;
                }
            }
        }

        this.setState({
            columnWidths: newWidths
        });
    }

    private _toggleSort(columnIdx: number): void {
        const { sortColumn, sortDirection } = this.state;

        let newSortDirection = SortDirection.Ascending;
        if (columnIdx === sortColumn) {
            // toggling sort on existing column
            if (sortDirection === SortDirection.None) {
                newSortDirection = SortDirection.Ascending;
            } else if (sortDirection === SortDirection.Ascending) {
                newSortDirection = SortDirection.Descending;
            } else {
                newSortDirection = SortDirection.None;
            }
        }

        this._sortData(columnIdx, newSortDirection);
    }

    private _sortData(sortColumn: number, direction: SortDirection): void {
        const { data, columns } = this.props;
        const column = columns[sortColumn];
        let sortedData = [] as T[];
        if (data && direction !== SortDirection.None && column.compareCallback) {
            sortedData = data.slice();
            sortedData.sort((itemA: T, itemB: T) => {
                const result = column.compareCallback?.(itemA, itemB) ?? 0;
                return direction === SortDirection.Ascending ?
                    result :
                    -result;
            });
        }

        this.setState({
            sortColumn: direction === SortDirection.None ? -1 : sortColumn,
            sortDirection: direction,
            sortedData
        });
    }
}

export { TableComponent };
export default TableComponent;



// WEBPACK FOOTER //
// ./src/components/common/tableComponent.tsx